import { FC, useEffect, useRef } from 'react';
import { useInventoryState } from '../../contexts/inventory.context';
import { Grid, Chip, Box, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import PaginatedGrid from '../../../../components/paginatedGrid/PaginatedGrid';
import {
  TInventoryItem,
  TInventoryTransaction,
  TInventoryCategory,
} from 'src/apis/administration/administration.interfaces';
import { useTranslation } from 'src/hooks/useTranslationHook';

interface InventoryGridProps {
  viewType: 'items' | 'categories' | 'transactions';
  onRowClick: (
    data: TInventoryItem | TInventoryCategory | TInventoryTransaction
  ) => void;
  isLoading?: boolean;
}

const InventoryGrid: FC<InventoryGridProps> = ({
  viewType,
  onRowClick,
  isLoading = false,
}) => {
  const { translateText } = useTranslation();
  const {
    items,
    categories,
    transactions,
    isLoading: contextIsLoading,
  } = useInventoryState();
  const hasDataBeenFetched = useRef(false);

  useEffect(() => {
    if (!contextIsLoading && !isLoading) {
      hasDataBeenFetched.current = true;
    }
  }, [contextIsLoading, isLoading]);

  const getItemsGridColumns = (): GridColDef[] => {
    return [
      {
        field: 'itemName',
        headerName: translateText('administration.inventory.columns.itemName'),
        width: 200,
        renderCell: (params) => params.row.itemName,
      },
      {
        field: 'category',
        headerName: translateText('administration.inventory.columns.category'),
        width: 120,
        renderCell: (params) => params.row.category,
      },
      {
        field: 'subCategory',
        headerName: translateText(
          'administration.inventory.columns.subCategory'
        ),
        width: 130,
        renderCell: (params) => params.row.subCategory,
      },
      {
        field: 'quantity',
        headerName: translateText('administration.inventory.columns.quantity'),
        width: 100,
        renderCell: (params) => params.row.quantity,
      },
      {
        field: 'unit',
        headerName: translateText('administration.inventory.columns.unit'),
        width: 80,
        renderCell: (params) => params.row.unit,
      },
      {
        field: 'unitPrice',
        headerName: translateText('administration.inventory.columns.unitPrice'),
        width: 130,
        renderCell: (params) => `₹${params.row.unitPrice.toLocaleString()}`,
      },
      {
        field: 'totalValue',
        headerName: translateText(
          'administration.inventory.columns.totalValue'
        ),
        width: 140,
        renderCell: (params) => `₹${params.row.totalValue.toLocaleString()}`,
      },
      {
        field: 'location',
        headerName: translateText('administration.inventory.columns.location'),
        width: 150,
        renderCell: (params) => params.row.location,
      },
      {
        field: 'condition',
        headerName: translateText('administration.inventory.columns.condition'),
        width: 100,
        renderCell: (params) => (
          <Chip
            label={params.row.condition}
            color={
              params.row.condition === 'NEW'
                ? 'success'
                : params.row.condition === 'GOOD'
                  ? 'primary'
                  : 'default'
            }
            size="small"
          />
        ),
      },
      {
        field: 'isActive',
        headerName: translateText('administration.inventory.columns.status'),
        width: 100,
        renderCell: (params) => (
          <Chip
            label={
              params.row.isActive
                ? translateText('administration.inventory.status.active')
                : translateText('administration.inventory.status.inactive')
            }
            color={params.row.isActive ? 'success' : 'default'}
            size="small"
          />
        ),
      },
      {
        field: 'lastRestocked',
        headerName: translateText(
          'administration.inventory.columns.lastRestocked'
        ),
        width: 130,
        renderCell: (params) => params.row.lastRestocked?.toLocaleDateString(),
      },
      {
        field: 'nextRestockDate',
        headerName: translateText(
          'administration.inventory.columns.nextRestock'
        ),
        width: 130,
        renderCell: (params) =>
          params.row.nextRestockDate?.toLocaleDateString(),
      },
    ];
  };

  const getCategoriesGridColumns = (): GridColDef[] => {
    return [
      {
        field: 'name',
        headerName: translateText(
          'administration.inventory.columns.categoryName'
        ),
        width: 200,
        renderCell: (params) => params.row.name,
      },
      {
        field: 'description',
        headerName: translateText(
          'administration.inventory.columns.description'
        ),
        width: 250,
        renderCell: (params) => params.row.description,
      },
      {
        field: 'totalItems',
        headerName: translateText(
          'administration.inventory.columns.totalItems'
        ),
        width: 120,
        renderCell: (params) => params.row.totalItems,
      },
      {
        field: 'totalValue',
        headerName: translateText(
          'administration.inventory.columns.totalValue'
        ),
        width: 150,
        renderCell: (params) => `₹${params.row.totalValue.toLocaleString()}`,
      },
      {
        field: 'isActive',
        headerName: translateText('administration.inventory.columns.status'),
        width: 100,
        renderCell: (params) => (
          <Chip
            label={
              params.row.isActive
                ? translateText('administration.inventory.status.active')
                : translateText('administration.inventory.status.inactive')
            }
            color={params.row.isActive ? 'success' : 'default'}
            size="small"
          />
        ),
      },
    ];
  };

  const getTransactionsGridColumns = (): GridColDef[] => {
    return [
      {
        field: 'itemId',
        headerName: translateText('administration.inventory.columns.itemId'),
        width: 120,
        renderCell: (params) => params.row.itemId,
      },
      {
        field: 'transactionType',
        headerName: translateText('administration.inventory.columns.type'),
        width: 120,
        renderCell: (params) => (
          <Chip
            label={params.row.transactionType}
            color={
              params.row.transactionType === 'PURCHASE'
                ? 'success'
                : params.row.transactionType === 'TRANSFER'
                  ? 'info'
                  : params.row.transactionType === 'DAMAGE'
                    ? 'error'
                    : 'default'
            }
            size="small"
          />
        ),
      },
      {
        field: 'quantity',
        headerName: translateText('administration.inventory.columns.quantity'),
        width: 100,
        renderCell: (params) => params.row.quantity,
      },
      {
        field: 'unitPrice',
        headerName: translateText('administration.inventory.columns.unitPrice'),
        width: 130,
        renderCell: (params) => `₹${params.row.unitPrice.toLocaleString()}`,
      },
      {
        field: 'totalAmount',
        headerName: translateText(
          'administration.inventory.columns.totalAmount'
        ),
        width: 140,
        renderCell: (params) => `₹${params.row.totalAmount.toLocaleString()}`,
      },
      {
        field: 'transactionDate',
        headerName: translateText('administration.inventory.columns.date'),
        width: 120,
        renderCell: (params) =>
          params.row.transactionDate?.toLocaleDateString(),
      },
      {
        field: 'referenceNumber',
        headerName: translateText('administration.inventory.columns.reference'),
        width: 130,
        renderCell: (params) => params.row.referenceNumber,
      },
      {
        field: 'supplier',
        headerName: translateText('administration.inventory.columns.supplier'),
        width: 150,
        renderCell: (params) => params.row.supplier || 'N/A',
      },
      {
        field: 'paymentStatus',
        headerName: translateText(
          'administration.inventory.columns.paymentStatus'
        ),
        width: 130,
        renderCell: (params) => (
          <Chip
            label={params.row.paymentStatus}
            color={
              params.row.paymentStatus === 'PAID'
                ? 'success'
                : params.row.paymentStatus === 'PENDING'
                  ? 'warning'
                  : params.row.paymentStatus === 'OVERDUE'
                    ? 'error'
                    : 'default'
            }
            size="small"
          />
        ),
      },
      {
        field: 'performedBy',
        headerName: translateText(
          'administration.inventory.columns.performedBy'
        ),
        width: 120,
        renderCell: (params) => params.row.performedBy,
      },
    ];
  };

  const getGridColumns = () => {
    switch (viewType) {
      case 'items':
        return getItemsGridColumns();
      case 'categories':
        return getCategoriesGridColumns();
      case 'transactions':
        return getTransactionsGridColumns();
      default:
        return getItemsGridColumns();
    }
  };

  const getGridRows = () => {
    switch (viewType) {
      case 'items':
        return items;
      case 'categories':
        return categories;
      case 'transactions':
        return transactions;
      default:
        return items;
    }
  };

  const handleRowClick = (params: any) => {
    onRowClick(params.row);
  };

  const rows = getGridRows();
  const columns = getGridColumns();

  if (isLoading || contextIsLoading) {
    return (
      <Box
        sx={{ p: 3, textAlign: 'center' }}
        data-testid={`inventory-grid-${viewType}-loading`}
      >
        <Typography>
          {translateText('administration.inventory.loading')}
        </Typography>
      </Box>
    );
  }

  // Only show "no data" if data has been fetched and rows are empty
  if (hasDataBeenFetched.current && rows.length === 0) {
    return (
      <Box
        sx={{ p: 3, textAlign: 'center' }}
        data-testid={`inventory-grid-${viewType}-no-data`}
      >
        <Typography>
          {translateText('administration.inventory.noData')}
        </Typography>
      </Box>
    );
  }

  return (
    <Grid container spacing={{ xs: 1, sm: 2 }} data-testid={`inventory-grid-${viewType}`}>
      <Grid item xs={12}>
        <PaginatedGrid
          dataTestId={`inventory-${viewType}-grid`}
          rows={rows}
          columns={columns}
          onRowClick={handleRowClick}
          getRowClassName={(params) => 'cursor-pointer'}
          hideColumnsOnMobile={['id', 'actions']}
        />
      </Grid>
    </Grid>
  );
};

export default InventoryGrid;
